<template>
  <v-dialog
    v-model="editApplicationDialog"
    no-click-animation
    overlay-color="rgba(39, 50, 64)"
    overlay-opacity="0.6"
    :fullscreen="$vuetify.breakpoint.xs ? true : false"
    persistent
    width="650"
    :transition="false"
  >
    <v-card>
      <v-card-title class="pl-5">
        {{ $t("modules.productCategories") }}
      </v-card-title>
      <v-card-text>
        <v-layout
          class="align-center"
          md6
          xs12
          mt-2
          pr-4
        >
          <v-flex
            v-if="form.user"
            md6
            xs12
            mt-2
            pr-4
          >
            <span>
              <b>{{ form.user.name }}</b>
              <br>
              <small>{{ form.user.email }}</small>
            </span>
          </v-flex>
          <v-flex
            md6
            xs12
            mt-2
            pr-4
          >
            <ApplicationPurchase
              v-if="form.product"
              :application="form.product"
            />
          </v-flex>
          <v-flex
            v-if="!form.user && !editLicense"
            md6
            xs12
            mt-2
            pr-4
          >
            <span>
              <b
                :class="
                  item.status == 'accepted'
                    ? 'text-positive'
                    : item.status == 'declined'
                      ? 'text-negative'
                      : ''
                "
              >
                {{ $t(`modules.${form.status}`) }}
              </b>
            </span>
          </v-flex>
          <v-flex
            v-if="editLicense && formStatus.status"
            md6
            xs12
            mt-2
            pr-4
          >
            <span>
              <b
                :class="
                  formStatus.status == 'accepted'
                    ? 'text-positive'
                    : formStatus.status == 'declined'
                      ? 'text-negative'
                      : ''
                "
              >
                {{ $t(`modules.${formStatus.status}`) }}
              </b>
            </span>
          </v-flex>
        </v-layout>
        <v-form>
          <v-layout wrap>
            <v-flex
              v-if="!editLicense"
              xs12
              mt-5
            >
              <v-select
                v-model="formStatus.status"
                :items="statusList"
                :item-text="'name'"
                :item-value="'value'"
                :label="$t('modules.applicationsSelectStatus') + '*'"
                :error-messages="StatusErrors"
                @input="$v.formStatus.status.$touch()"
                @blur="$v.formStatus.status.$touch()"
              />
            </v-flex>
            <v-flex
              v-if="formStatus.status === 'accepted'"
              xs12
              :class="editLicense ? 'mt-5' : ''"
            >
              <v-text-field
                v-model="formStatus.license_key"
                :label="$t('modules.applicationsLicenseKey') + '*'"
                :error-messages="LicenseErrors"
                @input="$v.formStatus.license_key.$touch()"
                @blur="$v.formStatus.license_key.$touch()"
              />
            </v-flex>
            <v-flex
              xs12
              mt-5
            >
              <div
                v-for="(field, i) in schema"
                :key="i"
                class="mt-3"
              >
                <NestedField
                  :ref="field.key"
                  :field="field"
                  :values="schemaValues"
                />
              </div>
            </v-flex>
          </v-layout>
        </v-form>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          outlined
          @click="editApplicationDialog = false"
        >
          {{ $t("modules.closeButton") }}
        </v-btn>
        <v-btn
          class="primary"
          :disabled="loading || $v.$invalid"
          :loading="loading"
          @click="save()"
        >
          {{ $t("modules.saveButton") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import LICENSES_CONFIG_SCHEMA_QUERY from "@/graphql/queries/modules/LicenseConfigSchema.gql";
import LICENSES_REQUEST_UPDATE_MUTATION from "@/graphql/mutations/modules/LicenseUpdateRequest.gql";
import LICENSES_UPDATE_MUTATION from "@/graphql/mutations/modules/LicenseUpdate.gql";
import { validationMixin } from "vuelidate";
import { required, maxLength, requiredIf } from "vuelidate/lib/validators";
import ApplicationPurchase from "@/views/Modules/Licenses/partial/Purchase.vue";
import NestedField from "@/components/core/NestedField.vue";

export default {
  name: "EditRequestStatus",
  components: {
    ApplicationPurchase,
    NestedField,
  },
  mixins: [validationMixin],
  props: {
    showDialog: {
      type: Boolean,
      default: false,
    },
    application: {
      type: Object,
      default() {
        return null;
      },
    },
    whitelabelUuid: {
      type: String,
      default: null,
    },
    editLicense: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      loading: false,
      formStatus: {
        status: "",
        license_key: "",
      },
      form: {},
      validatedSchema: true,
      statusList: [
        { name: this.$t("modules.accepted"), value: "accepted" },
        { name: this.$t("modules.declined"), value: "declined" },
      ],
    };
  },
  validations() {
    return {
      formStatus: {
        status: {
          required,
        },
        license_key: {
          required: requiredIf(function () {
            return this.formStatus.status === "accepted";
          }),
          maxLength: maxLength(500),
        },
      },
    };
  },
  computed: {
    schema() {
      return this.moduleLicenseConfigSchema
        ? JSON.parse(this.moduleLicenseConfigSchema.custom_config_schema)
        : [];
    },
    schemaValues() {
      return this.form && this.form.custom_values
        ? JSON.parse(this.form.custom_values)
        : {};
    },
    editApplicationDialog: {
      get() {
        return this.showDialog;
      },
      set(val) {
        this.$emit("closeEditRequestDialog", val);
      },
    },
    StatusErrors() {
      const errors = [];
      if (!this.$v.formStatus.status.$dirty) return errors;
      !this.$v.formStatus.status.required &&
        errors.push(this.$t("modules.reqField"));
      return errors;
    },
    LicenseErrors() {
      const errors = [];
      if (!this.$v.formStatus.license_key.$dirty) return errors;
      !this.$v.formStatus.license_key.required &&
        errors.push(this.$t("modules.reqField"));
      !this.$v.formStatus.license_key.maxLength &&
        errors.push(
          this.$t("modules.maxError", {
            char: this.$v.formStatus.license_key.$params.maxLength.max,
          })
        );
      return errors;
    },
  },
  mounted() {
    const form = JSON.parse(JSON.stringify(this.application));
    if (form) {
      if(this.editLicense) {
        this.formStatus.status = form.status === "active" ? 'accepted' : "declined";
        this.formStatus.license_key = form.license_key;
      }
      this.form = form;
    } else {
      this.form = {
        uuid: "",
        product: {
          name: "",
          description: "",
          image: "",
        },
        user: {
          name: "",
          email: "",
        },
        custom_values: null,
        status: "",
        created_at: "",
      };
    }
  },
  methods: {
    validateField(field) {
      if (field.$v !== undefined) {
        field.$v.$touch();
        if (field.$v.$invalid) {
          this.validatedSchema = false;
          return -1;
        }
      }
    },
    checkArrayFields(fields, refs) {
      return fields.reduce((config, field) => {
        refs[field.key].forEach((ref, ind) => {
          if (!config[ind]) {
            config[ind] = {};
          }
          config[ind][field.key] = this.checkFields(
            field,
            ref.$refs[field.key]
          );
        });
        return config;
      }, []);
    },
    checkFields(schema, refs, config = {}) {
      const isArrayItem = (item) => {
        return item.field === "object" && item.children;
      };
      const isEmptyCheckbox = (item, field) => {
        return item.field === "checkbox" && field[item.key] == null;
      };
      const getSimpleProperty = (propertyId, field) => {
        return parseFloat(field[propertyId]) || field[propertyId];
      };
      if (Array.isArray(schema)) {
        schema.forEach((field) => {
          if (isArrayItem(field)) {
            config[field.key] = this.checkArrayFields(
              field.children,
              refs[field.key][0].$refs
            );
          } else {
            config[field.key] = this.checkFields(
              field,
              refs[field.key][0].$refs[field.key],
              config
            );
          }
        });
      } else {
        if (isEmptyCheckbox(schema, refs)) {
          refs[schema.key] = false;
        }
        this.validateField(refs);
        return getSimpleProperty(schema.key, refs);
      }
      return config;
    },
    async save() {
      this.$v.$touch();
      if (this.$v.$invalid) return;

      this.form.custom_values = await JSON.stringify(
        this.checkFields(this.schema, this.$refs)
      );

      if (this.validatedSchema) {
        this.loading = true;
        let params = {};
        if(this.editLicense) {
          params = {
            whitelabel_uuid: this.whitelabelUuid,
            license_uuid: this.form.uuid,
            license_key: this.formStatus.license_key,
            custom_values: this.form.custom_values,
          };
        } else {
          params = {
            whitelabel_uuid: this.whitelabelUuid,
            request_uuid: this.form.uuid,
            license_key: this.formStatus.license_key,
            status: this.formStatus.status,
            custom_values: this.form.custom_values,
          };
        }
        this.$apollo
          .mutate({
            mutation: this.editLicense ? LICENSES_UPDATE_MUTATION : LICENSES_REQUEST_UPDATE_MUTATION,
            variables: params,
          })
          .then(() => {
            this.$store.commit("app/setNotifier", this.$t("globals.success"));
            this.loading = false;
            this.editApplicationDialog = true;
          })
          .catch((error) => {
            this.errorHandler(error);
            this.loading = false;
            this.editApplicationDialog = false;
          });
      }
    },
  },
  apollo: {
    moduleLicenseConfigSchema: {
      fetchPolicy: "network-only",
      query() {
        return LICENSES_CONFIG_SCHEMA_QUERY;
      },
      variables() {
        return {
          whitelabel_uuid: this.whitelabelUuid,
        };
      },
      skip() {
        return !this.whitelabelUuid;
      },
    },
  },
};
</script>

<style lang="scss" scoped></style>
